import { useContext, useState } from 'react'
import { ScrollView, Text, View } from 'react-native'
import { useMutation, useQuery } from '@apollo/client'
import { Redirect, useLocalSearchParams, usePathname } from 'expo-router'
import { BigNumber } from 'mathjs'
import { useTranslation } from 'react-i18next'

import { USER_PAYMENT_GATE_WAY_TRANSFER } from '@/constants/graphql/mutations'
import { GET_PAYMENT_GATEWAY_DATA_BY_INTERNAL_KEY, GET_USER, GET_WALLET_BY_CURRENCY } from '@/constants/graphql/queries'
import { formatTokenAmount } from '@/config/marketPresentation'
import { convertToDate } from '@/utils/formatter'
import { fundsPinErrorMessage } from '@/utils/fundsPinError'
import { useRouter } from '@/utils/navigation'
import { getBignumber } from '@/utils/numbers'
import { AuthContext } from '@/stores/auth'
import { Button, ModalFooter, ModalHeader, ModalTitle, useModal, useToast } from '@/components/base'
import BottomActionPanel from '@/components/BottomActionPanel'
import DescriptionListItem from '@/components/DescriptionListItem'
import { CheckCircle, Withdrawal } from '@/components/icon'
import { Stack } from '@/components/layout'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import LoadingScreen from '@/components/LoadingScreen'
import NavigationBar from '@/components/NavigationBar'
import NotFoundScreen from '@/components/NotFoundScreen'
import SectionHeader from '@/components/SectionHeader'
import { SecurityVerificationContext } from '@/components/SecurityVerificationScreen'
import SetTxPasswordModal from '@/components/SetTxPasswordModal'
import { useSuccessScreen } from '@/components/SuccessScreen'

type TransferRequest = {
  order: string
  partnerId: string
  amount: string
  createdAt: string
}

export default function PaymentLandingScreen() {
  const pathname = usePathname()
  const auth = AuthContext.useActorRef()
  const params = useLocalSearchParams<Record<string, string>>()
  const { systemId } = params

  if (!auth.getSnapshot().matches('LOGGED_IN')) {
    const params = { redirect: encodeURIComponent(pathname), params: encodeURIComponent(JSON.stringify({ systemId })) }
    return <Redirect href={{ pathname: '/login', params }} />
  }
  if (!systemId) {
    return <NotFoundScreen />
  }
  return <PaymentScreen systemId={systemId} />
}

function PaymentScreen({ systemId }: { systemId: string }) {
  const { t } = useTranslation()
  const loading = useLoadingIndicator()
  const {
    open: openVerificationModal,
    close: closeVerificationModal,
    setCb,
    useFormReturnValue: { setError }
  } = useContext(SecurityVerificationContext)
  const { open: openSuccessScreen, close: closeSuccessScreen } = useSuccessScreen()
  const [availableBalance, setAvailableBalance] = useState<BigNumber | null>(null)
  const [availableBonusBalance, setAvailableBonusBalance] = useState<BigNumber | null>(null)
  const [transferRequest, setTransferRequest] = useState<TransferRequest | null>(null)
  const toast = useToast()
  const { openModal, closeModal } = useModal()
  const router = useRouter()

  const user = AuthContext.useSelector((state) => state.context.values?.user)
  const { data: userData } = useQuery(GET_USER, {
    variables: {
      id: user?.id
    }
  })

  // Query Wallet
  const { loading: queryWalletLoading } = useQuery(GET_WALLET_BY_CURRENCY, {
    pollInterval: 10000,
    variables: {
      currencyKind: 'YBB'
    },
    onCompleted: (data) => {
      const wallet = data?.getWalletByCurrency
      const availableBalance = getBignumber(wallet?.balance.value)
      const bonusBalance = getBignumber(wallet?.bonusBalance?.value ?? '0')

      setAvailableBalance(availableBalance)
      setAvailableBonusBalance(bonusBalance)
    },
    // TODO: Handle error
    onError: (error) => {
      const code = error?.graphQLErrors[0]?.extensions?.code

      switch (code) {
        case 2000:
          setAvailableBalance(getBignumber(0))
      }
    }
  })

  // Get Payment Order
  const paymentGatewayVariables = {
    internalKey: systemId
  }
  const { loading: queryPaymentLoading, error } = useQuery(GET_PAYMENT_GATEWAY_DATA_BY_INTERNAL_KEY, {
    variables: paymentGatewayVariables,
    onCompleted: (data) => {
      const request = data?.getPaymentGatewayDataByInternalKey
      setTransferRequest(request)
    },
    onError: () => {
      toast.show({ variant: 'error', description: t('paymentFlow:loadFailed') })
    }
  })

  // Pay mutation
  const [submitPayment] = useMutation(USER_PAYMENT_GATE_WAY_TRANSFER, {
    onCompleted: (data) => {
      openSuccessScreen({
        children: (
          <View className={'items-center gap-3 py-4'}>
            <CheckCircle className={'size-20 text-text-success'} />
            <View className={'items-center pb-3'}>
              <Text className={'text-2xl font-semibold text-text-primary'}>{t('paymentFlow:paymentCompleted')}</Text>
            </View>
          </View>
        ),
        onClose: () => {
          closeVerificationModal()
          closeSuccessScreen()
          router.dismissAll()
          router.replace('/home')
        }
      })
      loading.stop()
    },
    onError: (error) => {
      const pinErrorMessage = fundsPinErrorMessage(error)
      if (pinErrorMessage) {
        setError('withdrawalPassword', { message: pinErrorMessage })
        loading.stop()
        return
      }
      const code = error?.graphQLErrors[0]?.extensions?.code
      if (code === 2020) {
        toast.show({
          variant: 'error',
          description: t('paymentFlow:unverifiedTradeFailed')
        })
      } else {
        toast.show({ variant: 'error', description: t('paymentFlow:paymentFailed') })
      }
      loading.stop()
    }
  })

  const onConfirm = () => {
    const spendableTotal = (availableBalance ?? getBignumber(0)).add(availableBonusBalance ?? getBignumber(0))
    const hasEnoughBalance = getBignumber(transferRequest?.amount).lessThanOrEqualTo(spendableTotal) ?? false
    const hasWithdrawalPassword = !!userData?.user?.withdrawalPasswordEnabled
    if (!hasEnoughBalance) {
      openModal({
        children: <InsufficientBalanceModal closeModal={closeModal} />,
        onClose: closeModal
      })
      return
    }
    if (!hasWithdrawalPassword) {
      openModal({
        children: <SetTxPasswordModal />,
        onClose: closeModal
      })
      return
    }
    setCb(() => onSubmit)
    openVerificationModal()
  }

  const onSubmit = (withdrawalPassword: string) => {
    loading.start()
    const variables = {
      internalKey: systemId,
      withdrawalPassword
    }
    submitPayment({
      variables
    })
  }

  const handleBack = () => {
    router.dismissAll()
    router.back()
  }

  if (queryWalletLoading || queryPaymentLoading) {
    return <LoadingScreen />
  }

  if (error || !transferRequest) {
    return <NotFoundScreen />
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => <NavigationBar handleAction={handleBack} />
        }}
      />

      <View className={'flex-1 bg-background-secondary'}>
        <ScrollView className={'pb-[72px]'}>
          <View className={'items-center gap-2 border-b border-border-10 bg-background-primary pb-[11px] pt-2'}>
            <Withdrawal className={'size-10 text-primary'} />
            <Text className={'text-2xl font-semibold text-text-primary'}>{t('paymentFlow:confirmPayment')}</Text>
            <View className={'items-center justify-center py-1'}>
              <View className={'flex-row gap-2 rounded bg-primary-light px-3 py-1'}>
                <Text className={'text-sm text-primary'}>{t('paymentFlow:myBalance')}</Text>
                <Text className={'font-roboto-medium text-sm text-primary'}>
                  {availableBalance ? formatTokenAmount(availableBalance.toString()) : '-'}
                </Text>
              </View>
              <View className={'flex-row gap-2 rounded bg-primary-subtle px-3 py-1'}>
                <Text className={'text-sm text-primary'}>{t('paymentFlow:bonusBalance')}</Text>
                <Text className={'font-roboto-medium text-sm text-primary'}>
                  {availableBonusBalance ? formatTokenAmount(availableBonusBalance.toString()) : '-'}
                </Text>
              </View>
              <View className={'flex-row gap-2 rounded bg-primary px-3 py-1'}>
                <Text className={'text-sm text-white'}>{t('paymentFlow:totalPayable')}</Text>
                <Text className={'font-roboto-medium text-sm text-white'}>
                  {availableBalance && availableBonusBalance
                    ? formatTokenAmount(availableBalance.add(availableBonusBalance).toString())
                    : '-'}
                </Text>
              </View>
            </View>
          </View>
          <SectionHeader>{t('orders:orderInformation')}</SectionHeader>
          <DescriptionListItem label={t('orders:systemOrderNumber')} hasBorderTop={true} canCopy={true}>
            {systemId}
          </DescriptionListItem>
          <DescriptionListItem label={t('orders:createdAt')}>
            {transferRequest ? convertToDate(new Date(transferRequest?.createdAt).valueOf()) : '-'}
          </DescriptionListItem>
          <DescriptionListItem label={t('wallet:paymentQuantity')} hasBorderBottom={true}>
            {transferRequest?.amount ? formatTokenAmount(transferRequest.amount) : '-'}
          </DescriptionListItem>
        </ScrollView>
        <BottomActionPanel>
          <Button
            disabled={queryWalletLoading || queryPaymentLoading}
            size={'md'}
            variant={'solid'}
            intent={'primary'}
            onPress={onConfirm}
            className={'flex-1'}
          >
            {t('paymentFlow:confirmPayment')}
          </Button>
        </BottomActionPanel>
      </View>
    </>
  )
}

function InsufficientBalanceModal({ closeModal }: { closeModal: () => void }) {
  const { t } = useTranslation()
  return (
    <>
      <ModalHeader className={'p-4'}>
        <ModalTitle>{t('paymentFlow:paymentFailed')}</ModalTitle>
      </ModalHeader>
      <View className={'px-4 py-2'}>
        <Text className={'text-center text-sm text-text-secondary'}>{t('paymentFlow:insufficientAvailableBalance')}</Text>
      </View>
      <ModalFooter>
        <Button className={'flex-1'} size={'md'} onPress={closeModal}>
          {t('c2c:gotIt')}
        </Button>
      </ModalFooter>
    </>
  )
}
